import { useEffect, useMemo } from 'react'
import { shallow } from 'zustand/shallow'
import useAppStore from '@/store/useAppStore'
import { useSearchTopics, useSearchUsers } from '@/hooks/api/dashboard'
import { SearchConnection, Topic } from '@/types'
import { TopicType } from '@/utils/constants'
import { topicToAlbumSummary, topicToGallerySummary } from '@/features/album/mappers'
import { AlbumSummary, GallerySummary } from '@/features/album/types'

export interface GlobalSearchResults {
  albums: AlbumSummary[]
  galleries: GallerySummary[]
  people: SearchConnection[]
  /** True while either the topic or people request is in flight for the current key. */
  loading: boolean
  /** True once a non-empty key has actually been submitted (vs. the empty "type something" state). */
  hasSearched: boolean
}

/**
 * Global "search everything" — the same `search/search` endpoint that backs
 * the People tab on `/connections`, but also split into albums vs. galleries
 * (both come back as `myTopics`, distinguished only by `topicType`) so the
 * `/search` results page and the top-bar's Enter-to-search can share one
 * implementation instead of each page rolling its own topic/user search glue.
 */
export function useGlobalSearch(key: string): GlobalSearchResults {
  const trimmedKey = key.trim()
  const searchTopics = useSearchTopics(trimmedKey)
  const searchUsers = useSearchUsers(trimmedKey)
  const [topicSearchResult, searchConnections] = useAppStore(
    (state) => [state.search.topicSearchResult, state.user.searchConnections],
    shallow,
  )

  useEffect(() => {
    if (!trimmedKey) return
    searchTopics.mutate(1)
    searchUsers.mutate(1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trimmedKey])

  const topics = useMemo<Topic[]>(() => Object.values(topicSearchResult ?? {}), [topicSearchResult])

  const albums = useMemo(
    () =>
      topics
        .filter((t) => String(t.topicType) === String(TopicType.ALBUM))
        .map((t) => topicToAlbumSummary(t)),
    [topics],
  )

  const galleries = useMemo(
    () =>
      topics
        .filter((t) => String(t.topicType) === String(TopicType.GALLERY))
        .map((t) => topicToGallerySummary(t)),
    [topics],
  )

  const people = useMemo<SearchConnection[]>(() => Object.values(searchConnections ?? {}), [searchConnections])

  return {
    albums,
    galleries,
    people,
    loading: trimmedKey.length > 0 && (searchTopics.isLoading || searchUsers.isLoading),
    hasSearched: trimmedKey.length > 0,
  }
}
